import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GlobalService } from '../../services/global.service';
import { MasterService } from '../../services/master.service';
import { CheckedInService } from 'src/app/users/services/checked-in.service';
import { JwtService } from '../../services/jwt.service';
import { ManageBrandingModel } from '../../models/request/manageBranding.model';
import { Globals } from '../../globals';
import countryCode from 'src/assets/user/countrycode/cc.json';
import countryData from 'src/assets/user/countrycode/data.json';

interface SelectedOption {
  _id?: string;
  Lang?: string;
  Value?: string;
  SelectedValue?: boolean;
}

interface SelectedPreference {
  id?: string;
  preferenceTypeId?: string;
  PreferenceTypeId?: string;
  PreferenceOptionType?: string;
  title?: string;
  Title?: string;
  Description?: string;
  PreferenceToggleValue?: boolean;
  PreferenceSelectionValue?: string;
  PreferenceSelectionArray?: string[];
  PreferenceOptions?: SelectedOption[];
}

interface SelectedPreferenceView {
  id: string;
  title: string;
  type: string;
  sourceIndex: number;
  items: Array<{
    value: string;
    checked: boolean;
    optionId?: string;
    isTextSelection?: boolean;
  }>;
}

@Component({
  selector: 'app-selected-preferences',
  templateUrl: './selected-preferences.component.html',
  styleUrls: ['./selected-preferences.component.scss'],
})
export class SelectedPreferencesComponent {
  displayedColumns: string[] = ['count', 'item', 'action'];
  selectedPreferences: SelectedPreference[] = [];
  selectedPreferenceViews: SelectedPreferenceView[] = [];
  public cc = countryCode;
  public countryData = countryData;
  public selectedDialCode = '+91';
  public selectedCode = 'in';
  public min;
  public max;
  public guestDetail;
  public mobileForm: FormGroup;
  public otpForm: FormGroup;
  public requestForm: FormGroup;
  public loginGuestDetails;
  public otp = '';
  public timerValue = 120;
  public timer: any;
  public isSubmitting = false;
  public isOtpSent = false;
  public isOtpVerified = false;
  public fullPhoneNumber = '';
  public preferenceTypeMap: { [key: string]: string } = {};
  brandingSubscribe$;

  public manageBrandingModel: ManageBrandingModel = new ManageBrandingModel();
  private destroy$ = new Subject<void>();

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  constructor(
    private fb: FormBuilder,
    public globalService: GlobalService,
    private masterService: MasterService,
    private router: Router,
    public checkedInService: CheckedInService,
    private jwtService: JwtService,
    public globals: Globals,
  ) {
    this.mobileForm = this.fb.group({
      fullName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
          Validators.pattern('^[a-zA-Z ]+$'),
        ],
      ],
      countryCode: [this.selectedDialCode, [Validators.required]],
      phoneNumber: ['', [Validators.required]],
      bookingRef: [''],
    });

    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.min(0), Validators.max(9)]],
      otp2: ['', [Validators.required, Validators.min(0), Validators.max(9)]],
      otp3: ['', [Validators.required, Validators.min(0), Validators.max(9)]],
      otp4: ['', [Validators.required, Validators.min(0), Validators.max(9)]],
      otp5: ['', [Validators.required, Validators.min(0), Validators.max(9)]],
      otp6: ['', [Validators.required, Validators.min(0), Validators.max(9)]],
    });

    this.requestForm = this.fb.group({
      roomNumber: [
        '',
        [
          Validators.required,
          Validators.maxLength(6),
          Validators.pattern('^[a-zA-Z0-9]{1,6}$'),
        ],
      ],
      message: ['', [Validators.maxLength(64)]],
    });
  }

  ngOnInit(): void {
    this.loadSelectedPreferences();
    this.syncSelectedPreferencesFromServer();
    this.getPreferenceTypes();
    this.setupOtpWatcher();
    if (
      !this.globalService.hasCheckInAuthToken() &&
      !this.globalService.hasLoginInAuthToken()
    ) {
      this.fetchManageBranding();
    }
    if (
      !this.globalService.hasCheckInAuthToken() &&
      this.globalService.hasLoginInAuthToken()
    ) {
      this.getLoginGuestDetail();
    }
    if (this.globalService.hasCheckInAuthToken()) {
      this.fetchGuestDetail();
    } else {
      // Set default country code if no token
      const index = this.cc.findIndex(
        (code) => code.dial_code === this.selectedDialCode,
      );
      if (index !== -1) {
        this.countryCodeSelected(index);
      }
    }
  }

  fetchManageBranding() {
    this.masterService.getManageBranding({
      Lang: this.globals.hotelLangTranslate,
      Subdomain: this.globalService.getHostname(),
    });
    //Subscribe to Branding
    this.brandingSubscribe$ = this.masterService.manageBrandingData.subscribe(
      (response) => {
        if (response.Data) {
          this.manageBrandingModel =
            this.globalService.jsonConvert.deserializeObject(
              response.Data,
              ManageBrandingModel,
            );
          // this.brandingData=this.manageBrandingModel

          this.selectedDialCode = this.manageBrandingModel.CountryCode;
          for (let data of countryCode) {
            if (data.dial_code === this.manageBrandingModel.CountryCode)
              this.selectedCode = data.code.toLowerCase();
            break;
          }
          this.mobileForm.controls['countryCode'].setValue(
            this.selectedDialCode,
          );

          const index = this.cc.findIndex(
            (code) => code.dial_code === this.selectedDialCode,
          );
          if (index !== -1) {
            this.countryCodeSelected(index);
          }
        } else {
          this.globalService.handleApiError(response.error);
        }
      },
      (error) => {
        this.globalService.showLoader = false;
        this.globalService.handleApiError(error);
      },
    );
  }

  fetchGuestDetail() {
    const decoded = this.jwtService.decodeJwttoken(
      window.localStorage['CheckIn_TOKEN'],
    );

    const data = {
      BookingUserId: decoded['UserId'],
      HotelId: decoded['HotelId'],
    };

    this.checkedInService
      .getGuestDetail(data)
      .pipe(takeUntil(this.globalService.componentDestroyed(this)))
      .subscribe(
        (response) => {
          this.guestDetail = response.Data;
          console.log(this.guestDetail);

          // Auto-fill and disable fields when hasCheckInAuthToken is true
          if (this.guestDetail) {
            // Create full name from firstName and lastName
            const fullName =
              `${this.guestDetail.firstName || ''} ${this.guestDetail.lastName || ''}`.trim();

            // Patch values to the form
            this.mobileForm.patchValue({
              fullName: fullName,
              bookingRef: this.guestDetail.bookingId || '',
              countryCode: this.guestDetail.CountryCode || '+91',
              phoneNumber: this.guestDetail.phoneNumber || '',
            });
            this.requestForm.patchValue({
              roomNumber: this.guestDetail.roomNo || '',
            });

            // Disable the fields
            this.mobileForm.get('fullName')?.disable();
            this.mobileForm.get('bookingRef')?.disable();
            this.requestForm.get('roomNumber')?.disable();

            // Update country code selection for flag display
            const index = this.cc.findIndex(
              (code) =>
                code.dial_code === (this.guestDetail.CountryCode || '+91'),
            );
            if (index !== -1) {
              this.countryCodeSelected(index);
            }
          }
        },
        (error) => {
          this.globalService.handleApiError(error);
        },
      );
  }
  private getDecodedLoginToken(): any {
    const token =
      window.localStorage['LogIn_TOKEN'] ||
      window.sessionStorage['LogIn_TOKEN'];
    if (!token) {
      return null;
    }

    try {
      const parsedToken =
        typeof token === 'string' ? token.replace(/^"|"$/g, '') : token;
      return this.jwtService.decodeJwttoken(parsedToken);
    } catch {
      return null;
    }
  }
  getLoginGuestDetail() {
    this.globalService.showLoader = true;
    const decoded = this.getDecodedLoginToken();
    if (!decoded || !decoded['id']) {
      this.globalService.showLoader = false;
      return;
    }

    const data = {
      Id: decoded['id'],
    };
    this.checkedInService
      .getLoginGuestDetail(data)
      .pipe(takeUntil(this.globalService.componentDestroyed(this)))
      .subscribe(
        (response) => {
          if (response.Data) {
            this.globalService.showLoader = false;
            this.loginGuestDetails = response?.Data[0];
            if (this.loginGuestDetails) {
              // Create full name from firstName and lastName
              const fullName =
                `${this.loginGuestDetails?.FirstName || ''} ${this.loginGuestDetails?.LastName || ''}`.trim();

              // Patch values to the form
              this.mobileForm.patchValue({
                fullName: fullName,
                countryCode: this.loginGuestDetails?.countryCode || '+91',
                phoneNumber: this.loginGuestDetails?.PhoneNumber || '',
              });

              // Disable the fields
              this.mobileForm.get('fullName')?.disable();

              // Update country code selection for flag display
              const index = this.cc.findIndex(
                (code) =>
                  code.dial_code ===
                  (this.loginGuestDetails?.countryCode || '+91'),
              );
              if (index !== -1) {
                this.countryCodeSelected(index);
              }
            }
          }
        },
        (error) => {
          this.globalService.showLoader = false;
          this.globalService.handleApiError(error);
        },
      );
  }
  goToPreferences() {
    this.router.navigate(['/preferences']);
  }

  onPreferenceItemToggle(
    preferenceView: SelectedPreferenceView,
    item: {
      value: string;
      checked: boolean;
      optionId?: string;
      isTextSelection?: boolean;
    },
    checked: boolean,
  ): void {
    if (checked) {
      return;
    }

    const selectedPreference =
      this.selectedPreferences[preferenceView.sourceIndex];
    if (!selectedPreference) {
      return;
    }

    if (item.isTextSelection) {
      selectedPreference.PreferenceSelectionValue = '';
      selectedPreference.PreferenceSelectionArray = [];
    } else {
      const options = Array.isArray(selectedPreference.PreferenceOptions)
        ? selectedPreference.PreferenceOptions
        : [];

      selectedPreference.PreferenceOptions = options.filter(
        (option: SelectedOption) => {
          if (item.optionId) {
            return option?._id !== item.optionId;
          }
          return option?.Value !== item.value;
        },
      );

      const hasSelectedOptionAfterRemoval = (
        selectedPreference.PreferenceOptions || []
      ).some((option: SelectedOption) => !!option?.SelectedValue);

      if (!hasSelectedOptionAfterRemoval) {
        selectedPreference.PreferenceSelectionValue = '';
        selectedPreference.PreferenceSelectionArray = [];
      }
    }

    const hasSelectedOptions = (
      selectedPreference.PreferenceOptions || []
    ).some((option: SelectedOption) => !!option?.SelectedValue);
    const hasSelectionText = !!(
      selectedPreference.PreferenceSelectionValue || ''
    ).trim();
    selectedPreference.PreferenceToggleValue =
      hasSelectedOptions || hasSelectionText;

    if (!hasSelectedOptions && !hasSelectionText) {
      this.selectedPreferences.splice(preferenceView.sourceIndex, 1);
    }

    this.persistSelectedPreferences();
    this.buildPreferenceViews();

    if (!this.selectedPreferenceViews.length) {
      this.goToPreferences();
    }
  }

  get hasSelectedPreferences(): boolean {
    return !!this.selectedPreferenceViews.length;
  }

  get selectedCount(): number {
    return this.selectedPreferenceViews.length;
  }

  trackByPreferenceView(index: number, preference: SelectedPreferenceView): string {
    return preference.id || `${preference.sourceIndex}-${preference.title || 'Preference'}-${index}`;
  }

  get selectedPeriodLabel(): string {
    if (!this.selectedPreferences.length) {
      return 'Stay';
    }
    const uniqueLabels = Array.from(
      new Set(
        this.selectedPreferences.map((item: SelectedPreference) =>
          this.getPreferenceTypeLabel(
            item.PreferenceTypeId || item.preferenceTypeId,
          ),
        ),
      ),
    );
    return uniqueLabels.join(', ');
  }

  countryCodeSelected(index) {
    let min = '1';
    let max = '9';

    if (
      this.mobileForm.get('countryCode')?.value === this.cc[index].dial_code
    ) {
      this.selectedDialCode = this.cc[index].dial_code;
      this.selectedCode = this.cc[index].code.toLowerCase();

      for (let i = 0; i < this.countryData.length; i++) {
        if (this.countryData[i].cca2 === this.cc[index].code) {
          const phLength = this.countryData[i].phLength;
          const phLengthAlt = this.countryData[i].phLengthAlt;

          const minLength = Math.min(phLength, phLengthAlt || phLength);
          const maxLength = Math.max(phLength, phLengthAlt || phLength);

          min = '1' + '0'.repeat(minLength - 1);
          max = '9'.repeat(maxLength);

          this.min = min;
          this.max = max;
          this.mobileForm
            .get('phoneNumber')
            ?.setValidators([
              Validators.required,
              Validators.min(parseInt(min, 10)),
              Validators.max(parseInt(max, 10)),
            ]);
          this.mobileForm.get('phoneNumber')?.updateValueAndValidity();
          break;
        }
      }
    }
  }

  numberValidation(event) {
    let code;
    if (event.target.value) {
      for (let i = 0; i < this.cc.length; i++) {
        if (
          this.mobileForm.get('countryCode')?.value === this.cc[i].dial_code
        ) {
          code = this.cc[i].code;
          break;
        }
      }

      for (let j = 0; j < this.countryData.length; j++) {
        if (code === this.countryData[j].cca2) {
          const phLength = this.countryData[j].phLength;
          const phLengthAlt = this.countryData[j].phLengthAlt;
          const maxLen = Math.max(phLength, phLengthAlt || phLength);

          let inputVal = event.target.value.toString();
          if (inputVal.length > maxLen) {
            inputVal = inputVal.substring(0, maxLen);
            this.mobileForm.get('phoneNumber')?.setValue(inputVal);
          }
          break;
        }
      }
    }
  }

  onkeydown(event) {
    return (
      event.keyCode !== 69 && event.keyCode !== 189 && event.keyCode !== 187
    );
  }

  sendOtp() {
    if (this.mobileForm.invalid) {
      this.mobileForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const countryCodeValue = this.mobileForm.get('countryCode')?.value;
    const phoneNumberValue = (
      this.mobileForm.get('phoneNumber')?.value || ''
    ).toString();
    const fullNameValue = this.mobileForm.get('fullName')?.value;

    this.fullPhoneNumber = `${countryCodeValue} ${phoneNumberValue}`;

    this.masterService
      .sendVerificationCode({
        LangKey: this.globalService.gethotelLangTranslate(),
        name: fullNameValue,
        countryCode: countryCodeValue,
        phoneNumber: phoneNumberValue,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.isSubmitting = false;
          if (response && response.Status) {
            this.isOtpSent = true;
            this.isOtpVerified = false;
            this.clearOtpForm();
            this.startTimer();
            this.globalService.handleSuccessMessage(
              response.Message || 'OTP sent successfully',
            );
            return;
          }
          this.globalService.handleApiError(response, true);
        },
        (error) => {
          this.isSubmitting = false;
          this.globalService.handleApiError(error, true);
        },
      );
  }

  verifyOtp() {
    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    this.combineOtp();
    this.isSubmitting = true;
    const countryCodeValue = this.mobileForm.get('countryCode')?.value;
    const phoneNumberValue = (
      this.mobileForm.get('phoneNumber')?.value || ''
    ).toString();

    this.masterService
      .otpVerification({
        LangKey: this.globalService.gethotelLangTranslate(),
        Subdomain: this.globalService.getHostname(),
        countryCode: countryCodeValue,
        phoneNumber: phoneNumberValue,
        otp1: this.otpForm.get('otp1')?.value,
        otp2: this.otpForm.get('otp2')?.value,
        otp3: this.otpForm.get('otp3')?.value,
        otp4: this.otpForm.get('otp4')?.value,
        otp5: this.otpForm.get('otp5')?.value,
        otp6: this.otpForm.get('otp6')?.value,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.isSubmitting = false;
          if (response && response.Status) {
            this.isOtpVerified = true;
            this.globalService.handleSuccessMessage(
              response.Message || 'OTP verified successfully',
            );
            return;
          }
          this.globalService.handleApiError(response, true);
        },
        (error) => {
          this.isSubmitting = false;
          this.clearOtpForm()
          this.globalService.handleApiError(error, true);
        },
      );
  }

  resendOtp() {
    if (this.timerValue > 0 || this.isSubmitting) {
      return;
    }
    this.sendOtp();
  }

  wrongNumber() {
    this.isOtpSent = false;
    this.isOtpVerified = false;
    this.clearOtpForm();
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  requestPreferences() {
    if (!this.selectedPreferences.length) {
      this.globalService.handleErrorMessage('No preferences selected');
      return;
    }

    if (!this.isOtpVerified) {
      this.globalService.handleErrorMessage('Please verify OTP first');
      return;
    }

    if (this.requestForm.invalid || this.mobileForm.invalid) {
      this.requestForm.markAllAsTouched();
      this.mobileForm.markAllAsTouched();
      return;
    }

    const preferencePayload = this.selectedPreferences
      .map((item: SelectedPreference) => {
        const selectedOptions = (item.PreferenceOptions || [])
          .filter((option: SelectedOption) => !!option?.SelectedValue)
          .map((option: SelectedOption) => ({
            Lang: option.Lang || this.globalService.getLangSaveProperty(),
            Value: option.Value || '',
            _id: option._id,
            SelectedValue: true,
          }));

        const preferenceSelectionValue = item.PreferenceSelectionValue || '';
        const hasSelectedData =
          selectedOptions.length > 0 || !!preferenceSelectionValue;
        if (!hasSelectedData) {
          return null;
        }

        return {
          id: item.id,
          Title: item.Title || item.title || '',
          Description: item.Description || '',
          PreferenceTypeId:
            item.PreferenceTypeId || item.preferenceTypeId || '',
          PreferenceOptionType: item.PreferenceOptionType || '',
          PreferenceOptions: selectedOptions,
          PreferenceToggleValue:
            typeof item.PreferenceToggleValue === 'boolean'
              ? item.PreferenceToggleValue
              : hasSelectedData,
          PreferenceSelectionValue: preferenceSelectionValue,
          PreferenceSelectionArray: Array.isArray(item.PreferenceSelectionArray)
            ? item.PreferenceSelectionArray
            : [],
        };
      })
      .filter((item: any) => !!item);

    const payload = {
      Message: this.requestForm.get('message')?.value || '',

      BookingRef: (this.mobileForm.get('bookingRef')?.value || '').trim(),
      FullName: this.mobileForm.get('fullName')?.value,
      CountryCode: this.mobileForm.get('countryCode')?.value,
      PhoneNumber: (this.mobileForm.get('phoneNumber')?.value || '').toString(),
      RoomNumber: this.requestForm.get('roomNumber')?.value,
      Preference: preferencePayload,
    };
    this.globalService.showLoader = true;
    this.isSubmitting = true;
    this.masterService
      .guestRequestPreferences(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.isSubmitting = false;
          this.globalService.showLoader = false;
          if (response && response.Status) {
            this.globalService.handleSuccessMessage(
              response.Message || 'Preferences requested successfully',
            );
            const RequestId = response?.Data?.RequestId || '';
            sessionStorage.removeItem('selectedPreferences');
            this.router.navigate([
              '/thank-you',
              this.mobileForm.get('countryCode')?.value || '',
              this.mobileForm.get('phoneNumber')?.value || '',
              this.requestForm.get('roomNumber')?.value || '',
              RequestId
            ]);
            return;
          }
          this.globalService.handleApiError(response, true);
        },
        (error) => {
          this.isSubmitting = false;
          this.globalService.showLoader = false;
          this.globalService.handleApiError(error, true);
        },
      );
  }

  onOtpInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const idx = parseInt(input.getAttribute('data-idx') || '0', 10);
    input.value = input.value.slice(0, 1);
    if (input.value) {
      const nextInput = this.getNextInput(idx);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onOtpKeyDown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const idx = parseInt(input.getAttribute('data-idx') || '0', 10);

    if (
      !/^\d$/.test(event.key) &&
      !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace' && !input.value) {
      const prevInput = this.getPrevInput(idx);
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    }
  }

  private getNextInput(currentIdx: number): HTMLInputElement | null {
    const inputs = Array.from(document.querySelectorAll('.otp'));
    return currentIdx < 5 ? (inputs[currentIdx + 1] as HTMLInputElement) : null;
  }

  private getPrevInput(currentIdx: number): HTMLInputElement | null {
    const inputs = Array.from(document.querySelectorAll('.otp'));
    return currentIdx > 0 ? (inputs[currentIdx - 1] as HTMLInputElement) : null;
  }

  private setupOtpWatcher() {
    this.otpForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isOtpFilled()) {
        this.combineOtp();
      }
    });
  }

  private clearOtpForm() {
    this.otpForm.reset({
      otp1: '',
      otp2: '',
      otp3: '',
      otp4: '',
      otp5: '',
      otp6: '',
    });
    this.otp = '';
  }

  private isOtpFilled(): boolean {
    return Object.values(this.otpForm.value).every(
      (value) => value !== '' && value !== null && value !== undefined,
    );
  }

  private combineOtp() {
    this.otp = Object.values(this.otpForm.value).join('');
  }

  private startTimer() {
    if (this.timer) {
      clearInterval(this.timer);
    }

    this.timerValue = 120;
    this.timer = setInterval(() => {
      this.timerValue--;
      if (this.timerValue <= 0) {
        clearInterval(this.timer);
      }
    }, 1000);
  }

  formatTime(timeInSeconds: number): string {
    const minutes: number = Math.floor(timeInSeconds / 60);
    const seconds: number = timeInSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private getPreferenceTypes() {
    this.masterService
      .showAllPreferenceTypes({
        LangSave: this.globalService.getLangSaveProperty(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (response && response.Status && Array.isArray(response.Data)) {
            response.Data.forEach((item: any) => {
              const titleList = Array.isArray(item?.title) ? item.title : [];
              const localizedTitle = this.getLocalizedValue(titleList);
              if (item?._id && localizedTitle) {
                this.preferenceTypeMap[item._id] = localizedTitle;
              }
            });
          }
        },
        () => {},
      );
  }

  private syncSelectedPreferencesFromServer() {
    const preferenceTypeIds = Array.from(
      new Set(
        (this.selectedPreferences || [])
          .map(
            (item: SelectedPreference) =>
              item.PreferenceTypeId || item.preferenceTypeId,
          )
          .filter((item: string) => !!item),
      ),
    );

    if (!preferenceTypeIds.length) {
      return;
    }

    const requests = preferenceTypeIds.map((preferenceType: string) =>
      this.masterService.showAllPreferences({
        LangSave: this.globalService.getLangSaveProperty(),
        preferenceType,
      }),
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (responses) => {
          const preferenceMap = new Map<string, any>();
          responses.forEach((response: any) => {
            if (response?.Status && Array.isArray(response.Data)) {
              response.Data.forEach((preference: any) => {
                if (preference?._id) {
                  preferenceMap.set(preference._id, preference);
                }
              });
            }
          });

          this.selectedPreferences = (this.selectedPreferences || []).map(
            (item: SelectedPreference) => {
              const serverPreference = item?.id
                ? preferenceMap.get(item.id)
                : null;
              if (!serverPreference) {
                return item;
              }
              return {
                ...item,
                title:
                  this.getLocalizedValue(serverPreference.title || []) ||
                  item.title,
                Title:
                  this.getLocalizedValue(serverPreference.title || []) ||
                  item.Title ||
                  item.title,
                Description:
                  this.getLocalizedValue(serverPreference.description || []) ||
                  item.Description,
                PreferenceTypeId:
                  serverPreference.preferenceTypeId ||
                  item.PreferenceTypeId ||
                  item.preferenceTypeId,
                PreferenceOptionType:
                  serverPreference.preferenceOptionType ||
                  item.PreferenceOptionType,
              };
            },
          );

          this.buildPreferenceViews();
        },
        () => {},
      );
  }

  private getLocalizedValue(
    list: Array<{ Lang: string; Value: string }>,
  ): string {
    if (!Array.isArray(list) || !list.length) {
      return '';
    }
    const currentLang = this.globalService.getLangSaveProperty();
    const selected = list.find((item) => item.Lang === currentLang);
    return selected?.Value || list[0].Value || '';
  }

  private loadSelectedPreferences(): void {
    const rawData = sessionStorage.getItem('selectedPreferences');
    if (!rawData) {
      this.selectedPreferences = [];
      this.selectedPreferenceViews = [];
      return;
    }

    try {
      const parsed = JSON.parse(rawData);
      this.selectedPreferences = Array.isArray(parsed) ? parsed : [];
      this.buildPreferenceViews();
    } catch {
      this.selectedPreferences = [];
      this.selectedPreferenceViews = [];
    }
  }

  private persistSelectedPreferences(): void {
    sessionStorage.setItem(
      'selectedPreferences',
      JSON.stringify(this.selectedPreferences),
    );
  }

  private buildPreferenceViews(): void {
    this.selectedPreferenceViews = this.selectedPreferences
      .map((item: SelectedPreference, index: number) =>
        this.toPreferenceView(item, index),
      )
      .filter((item: SelectedPreferenceView) => item.items.length > 0);
  }

  private toPreferenceView(
    preference: SelectedPreference,
    sourceIndex: number,
  ): SelectedPreferenceView {
    const options = Array.isArray(preference.PreferenceOptions)
      ? preference.PreferenceOptions
      : [];
    const optionItems = options
      .filter(
        (option: SelectedOption) =>
          !!option && !!option.Value && !!option.SelectedValue,
      )
      .map((option: SelectedOption) => ({
        value: option.Value || '',
        checked: true,
        optionId: option._id,
        isTextSelection: false,
      }));

    if (optionItems.length) {
      return {
        id: preference.id || '',
        title: preference.Title || preference.title || 'Preference',
        type: preference.PreferenceOptionType || '',
        sourceIndex,
        items: optionItems,
      };
    }

    const textValue = (preference.PreferenceSelectionValue || '').trim();
    return {
      id: preference.id || '',
      title: preference.Title || preference.title || 'Preference',
      type: preference.PreferenceOptionType || '',
      sourceIndex,
      items: textValue
        ? [{ value: textValue, checked: true, isTextSelection: true }]
        : [],
    };
  }

  private getPreferenceTypeLabel(preferenceTypeId?: string): string {
    if (preferenceTypeId && this.preferenceTypeMap[preferenceTypeId]) {
      return this.preferenceTypeMap[preferenceTypeId];
    }
    switch (preferenceTypeId) {
      case '5fe7391a2b03e2067e0879b3':
        return 'During Stay';
      case '5cfa6365d43cc23fe0829e7f':
        return 'Before Stay';
      case '5fe739242b03e2067e0879b4':
        return 'After Stay';
      default:
        return 'Stay';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}
